import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { formatDate } from '../../config/appConfig';
import { Button, ChevronDownIcon, Hint, Progress, Select, useConfirm } from '../../components/ui';
import { PinIcon, PinOffIcon, SearchIcon, XIcon, DocumentTextIcon, UserIcon, TrashIcon, TableIcon, CogIcon } from '../../components/ui/Icons';
import { Modal, ModalContent, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import {
  TimelineHourHeader,
  TimelinePaintGrid,
  formatHm,
  mergeSegments,
  paintSegment,
  type TimeSegment,
} from '../../components/timeline-grid';
import { fetcher } from '../../services/fetcher';
import {
  clearWrhSheetDayIntervals,
  ensureWrhSheetDay,
  seedWrhSheetDayArrangements,
  updateWrhCrewDay,
  updateWrhIntervals,
} from '../../services/mutations';

type Interval = {
  startMinute: number;
  endMinute: number;
  startTime: string;
  endTime: string;
  isException?: boolean;
};

type CrewDayCell = {
  wrhDayId: number;
  crewDayId: number;
  status: string;
  workHours: number;
  restHours: number;
  workHours7d: number;
  restHours7d: number;
  overtimeHours: number;
  holidayFlag: boolean;
  notes: string | null;
  compliant: boolean;
  violationCodes: string[];
  /** Measured vs rule text from the server (optional for legacy rows). */
  violationDetails?: Record<string, string>;
  intervals: Interval[];
};

type CrewRow = {
  crewId: number;
  crewName: string;
  rankName?: string | null;
  watchkeeper: boolean;
  days: Record<string, CrewDayCell>;
};

type Overlay = {
  workDate: string;
  vesselStatus: string;
  segments: Array<{ startMinute: number; endMinute: number; color: string; name: string }>;
};

type Sheet = {
  vesselId: number;
  vesselName: string;
  vesselAbrv?: string | null;
  imoNumber?: string | null;
  flag?: { id: number; name: string; code?: string | null } | null;
  month: string;
  dates: string[];
  rules: {
    minRest24h: number;
    minRest7d: number;
    maxWork24h: number;
    maxRestSplits?: number;
    minLongestRestSegment?: number;
    maxGapBetweenRest?: number;
  };
  operationOverlays: Overlay[];
  crew: CrewRow[];
};

type CellSelection = {
  date: string;
  startMinute: number;
  endMinute: number;
};

const CELL_H = 30;
const DATE_COL = 88;
/** Half-hour slot width — fixed so paint targets stay easy to hit. */
const SLOT_W = 26;
const HOURS_W = 48 * SLOT_W;
/** Read-only compliance + editable notes (desktop WRH columns). */
const COL = {
  rest24: 52,
  work24: 52,
  restOk: 48,
  rest7d: 52,
  ot: 48,
  h: 34,
  notes: 128,
} as const;
const COMPLY_COLS_W =
  COL.rest24 + COL.work24 + COL.restOk + COL.rest7d + COL.ot + COL.h + COL.notes;

const VIOLATION_META: Record<
  string,
  { short: string; hint: (rules?: Sheet['rules']) => string; metric?: keyof typeof COL }
> = {
  REST_24H: {
    short: 'Rest 24h',
    hint: (r) => `Hours of rest in 24h are below the minimum (${Number(r?.minRest24h ?? 10)}h).`,
    metric: 'rest24',
  },
  WORK_24H: {
    short: 'Work 24h',
    hint: (r) => `Hours of work in 24h exceed the maximum (${Number(r?.maxWork24h ?? 14)}h).`,
    metric: 'work24',
  },
  REST_7D: {
    short: 'Rest 7d',
    hint: (r) => `Hours of rest in any 7-day period are below the minimum (${Number(r?.minRest7d ?? 77)}h).`,
    metric: 'rest7d',
  },
  REST_SPLIT: {
    short: 'Rest split',
    hint: (r) =>
      `In a sliding 24h window, rest is split into more than ${Number(r?.maxRestSplits ?? 2)} periods, or the longest rest is shorter than ${Number(r?.minLongestRestSegment ?? 6)}h. Overnight rest before the morning watch (e.g. 20:00–08:00) counts as one continuous block.`,
  },
  REST_GAP: {
    short: 'Rest gap',
    hint: (r) =>
      `In a sliding 24h window, the gap between rest periods exceeds the maximum (${Number(r?.maxGapBetweenRest ?? 14)}h).`,
  },
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function minuteLabel(m: number): string {
  const clamped = ((m % (24 * 60)) + 24 * 60) % (24 * 60);
  return `${pad2(Math.floor(clamped / 60))}:${pad2(clamped % 60)}`;
}

/** Derive rest gaps inside a calendar day from painted work intervals (fallback when server details are missing). */
function restSegmentsFromIntervals(intervals: Interval[]): Array<{ start: number; end: number; hours: number }> {
  const merged: Array<{ start: number; end: number }> = [];
  const sorted = [...intervals]
    .map((iv) => ({
      start: Math.max(0, iv.startMinute),
      end: Math.min(24 * 60, iv.endMinute > 24 * 60 ? 24 * 60 : iv.endMinute),
    }))
    .filter((iv) => iv.end > iv.start)
    .sort((a, b) => a.start - b.start);
  for (const iv of sorted) {
    const last = merged[merged.length - 1];
    if (!last || iv.start > last.end) merged.push({ ...iv });
    else last.end = Math.max(last.end, iv.end);
  }
  const segs: Array<{ start: number; end: number; hours: number }> = [];
  let cursor = 0;
  for (const m of merged) {
    if (m.start > cursor) {
      const hours = Math.round(((m.start - cursor) / 60) * 100) / 100;
      if (hours > 0.01) segs.push({ start: cursor, end: m.start, hours });
    }
    cursor = Math.max(cursor, m.end);
  }
  if (cursor < 24 * 60) {
    const hours = Math.round(((24 * 60 - cursor) / 60) * 100) / 100;
    if (hours > 0.01) segs.push({ start: cursor, end: 24 * 60, hours });
  }
  return segs;
}

/** Derive STCW rest periods (wrap overnight across midnight). */
function stcwRestPeriodsFromIntervals(intervals: Interval[]): Array<{
  start: number;
  end: number;
  hours: number;
  wrapsMidnight?: boolean;
}> {
  const raw = restSegmentsFromIntervals(intervals);
  if (raw.length < 2) return raw;
  const ordered = [...raw].sort((a, b) => a.start - b.start);
  const first = ordered[0];
  const last = ordered[ordered.length - 1];
  const touchesStart = first.start <= 0;
  const touchesEnd = last.end >= 24 * 60;
  if (!touchesStart || !touchesEnd) return ordered;
  const overnight = {
    start: last.start,
    end: first.end,
    hours: Math.round((first.hours + last.hours) * 100) / 100,
    wrapsMidnight: true as const,
  };
  return [...ordered.slice(1, -1), overnight];
}

function fallbackViolationDetail(
  code: string,
  cell: CrewDayCell,
  rules?: Sheet['rules'],
): string | null {
  if (code === 'REST_24H') {
    const min = Number(rules?.minRest24h ?? 10);
    return `Rest in 24h is ${Number(cell.restHours)}h (minimum ${min}h). Shortfall: ${Math.round((min - Number(cell.restHours)) * 100) / 100}h.`;
  }
  if (code === 'WORK_24H') {
    const max = Number(rules?.maxWork24h ?? 14);
    return `Work in 24h is ${Number(cell.workHours)}h (maximum ${max}h). Excess: ${Math.round((Number(cell.workHours) - max) * 100) / 100}h.`;
  }
  if (code === 'REST_7D') {
    const min = Number(rules?.minRest7d ?? 77);
    return `Rest over 7 days is ${Number(cell.restHours7d)}h (minimum ${min}h). Shortfall: ${Math.round((min - Number(cell.restHours7d)) * 100) / 100}h.`;
  }
  if (code === 'REST_SPLIT') {
    const maxSplits = Number(rules?.maxRestSplits ?? 2);
    const minLongest = Number(rules?.minLongestRestSegment ?? 6);
    const segs = stcwRestPeriodsFromIntervals(cell.intervals ?? []);
    const longest = segs.reduce((m, s) => Math.max(m, s.hours), 0);
    const parts: string[] = [];
    if (segs.length > maxSplits) {
      parts.push(`Rest is split into ${segs.length} periods (STCW maximum: ${maxSplits}).`);
    }
    if (segs.length > 0 && longest + 1e-9 < minLongest) {
      parts.push(`Longest continuous rest is ${longest}h (STCW requires one block ≥ ${minLongest}h).`);
    }
    if (segs.length) {
      parts.push(
        `Periods:\n${segs
          .map((s) =>
            s.wrapsMidnight
              ? `• ${minuteLabel(s.start)}–${minuteLabel(s.end)} (${s.hours}h, overnight)`
              : `• ${minuteLabel(s.start)}–${minuteLabel(s.end)} (${s.hours}h)`,
          )
          .join('\n')}`,
      );
    }
    if (segs.length > maxSplits) {
      parts.push(
        `Tip: STCW uses sliding 24h windows (not calendar midnight). Rest before the first watch — e.g. 20:00–08:00 overnight — counts as one continuous block.`,
      );
    }
    return parts.join('\n') || null;
  }
  return null;
}

function violationHint(
  code: string,
  rules?: Sheet['rules'],
  cell?: CrewDayCell | null,
): string {
  const fromServer = cell?.violationDetails?.[code];
  if (fromServer) return fromServer;
  if (cell) {
    const local = fallbackViolationDetail(code, cell, rules);
    if (local) return local;
  }
  const meta = VIOLATION_META[code];
  if (meta) return meta.hint(rules);
  return `Compliance issue: ${code}`;
}

type ComplyHeader = {
  key: keyof typeof COL;
  line1: string;
  line2?: string;
  title: string;
};

const COMPLY_HEADERS: ComplyHeader[] = [
  { key: 'rest24', line1: 'Rest', line2: '24h', title: 'Hours of rest in 24 hour period' },
  { key: 'work24', line1: 'Work', line2: '24h', title: 'Hours of work in 24 hour period' },
  {
    key: 'restOk',
    line1: '≥10h',
    line2: 'Y/N',
    title: 'Hours of rest in any 24 hours period (at least 10) Y/N',
  },
  { key: 'rest7d', line1: 'Rest', line2: '7d', title: 'Hours of rest in any 7 day period' },
  { key: 'ot', line1: 'OT', line2: 'hrs', title: 'Overtime — hours above 8h normal working day (all work hours on holidays)' },
  { key: 'h', line1: 'H', title: 'Holiday' },
  { key: 'notes', line1: 'Notes', title: 'Notes (editable)' },
];

const MONTH_OPTIONS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const nowParts = () => {
  const d = new Date();
  return {
    year: String(d.getFullYear()),
    month: String(d.getMonth() + 1).padStart(2, '0'),
  };
};

const toPeriodYm = (year: string, month: string) => `${year}-${month}`;


const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function buildSlotBackgrounds(overlay?: Overlay): Array<string | null> {
  const slots: Array<string | null> = Array.from({ length: 48 }, () => null);
  if (!overlay) return slots;
  for (const s of overlay.segments) {
    for (let m = s.startMinute; m < s.endMinute; m += 30) {
      const i = Math.floor(m / 30);
      if (i >= 0 && i < 48) slots[i] = s.color;
    }
  }
  return slots;
}

function weekdayLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`);
  return WEEKDAYS[d.getUTCDay()] ?? '';
}

function selectionKey(s: CellSelection): string {
  return `${s.date}:${s.startMinute}-${s.endMinute}`;
}

function mergeSelections(existing: CellSelection[], next: CellSelection, additive: boolean): CellSelection[] {
  if (!additive) return [next];
  const map = new Map<string, CellSelection>();
  for (const s of existing) map.set(selectionKey(s), s);
  map.set(selectionKey(next), next);
  // Merge overlapping ranges on the same date
  const byDate = new Map<string, TimeSegment[]>();
  Array.from(map.values()).forEach((s) => {
    const list = byDate.get(s.date) ?? [];
    list.push({ startMinute: s.startMinute, endMinute: s.endMinute });
    byDate.set(s.date, list);
  });
  const out: CellSelection[] = [];
  Array.from(byDate.entries()).forEach(([date, segs]) => {
    for (const m of mergeSegments(segs)) {
      out.push({ date, startMinute: m.startMinute, endMinute: m.endMinute });
    }
  });
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.startMinute - b.startMinute);
}

function fmtHours(n: number | undefined | null): string {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

/** Calendar-day work hours from painted intervals (clipped to 00:00–24:00). */
function calendarDayWorkHours(intervals: Interval[] | undefined): number {
  if (!intervals?.length) return 0;
  let minutes = 0;
  for (const iv of intervals) {
    const start = Math.max(0, iv.startMinute);
    const end = Math.min(24 * 60, iv.endMinute > 24 * 60 ? 24 * 60 : iv.endMinute);
    if (end > start) minutes += end - start;
  }
  return minutes / 60;
}

function roundTotal(n: number): number {
  return Math.round(n * 100) / 100;
}

function MetricCell({
  width,
  title,
  children,
  tone = 'default',
}: {
  width: number;
  title: string;
  children: React.ReactNode;
  tone?: 'default' | 'ok' | 'bad' | 'warn' | 'muted';
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-center border-l border-black/[0.06] px-0.5 text-[12px] tabular-nums dark:border-white/[0.08]',
        tone === 'default' && 'text-zinc-800 dark:text-zinc-100',
        tone === 'ok' && 'font-semibold text-emerald-700 dark:text-emerald-400',
        tone === 'bad' && 'font-semibold text-rose-700 dark:text-rose-400',
        tone === 'warn' && 'font-semibold text-amber-700 dark:text-amber-400',
        tone === 'muted' && 'text-zinc-400 dark:text-zinc-500',
      )}
      style={{ width }}
      title={title}
    >
      {children}
    </div>
  );
}

function YnPill({ ok }: { ok: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex h-5 min-w-[1.35rem] items-center justify-center rounded-md px-1 text-[10px] font-bold tracking-wide',
        ok
          ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
          : 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
      )}
    >
      {ok ? 'Y' : 'N'}
    </span>
  );
}

function fmtHoursShort(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `${Math.round(n * 100) / 100}h`;
}

type ComplianceCheck = {
  key: string;
  title: string;
  detail: string;
  ok: boolean;
  /** Rule-based fix tip when non-compliant (no AI required). */
  suggestion?: string;
};

function suggestionForCheck(
  key: string,
  ctx: {
    rest24: number;
    work24: number;
    rest7: number;
    minRest24: number;
    maxWork24: number;
    minRest7: number;
    maxSplits: number;
    minLongest: number;
    maxGap: number;
    segs: Array<{ start: number; end: number; hours: number; wrapsMidnight?: boolean }>;
    periodCount: number;
    longest: number;
  },
): string | undefined {
  const {
    rest24,
    work24,
    rest7,
    minRest24,
    maxWork24,
    minRest7,
    maxSplits,
    minLongest,
    maxGap,
    segs,
    periodCount,
    longest,
  } = ctx;

  if (key === 'REST_24H') {
    const need = Math.round((minRest24 - rest24) * 100) / 100;
    if (need > 0) {
      return `Suggestion: reduce work by at least ${fmtHoursShort(need)} (or leave ${fmtHoursShort(need)} more rest) so daily rest reaches ${minRest24}h.`;
    }
  }
  if (key === 'WORK_24H') {
    const excess = Math.round((work24 - maxWork24) * 100) / 100;
    if (excess > 0) {
      return `Suggestion: remove or shorten work by at least ${fmtHoursShort(excess)} so work stays at or below ${maxWork24}h.`;
    }
  }
  if (key === 'REST_SPLIT_COUNT') {
    const extras = Math.max(0, periodCount - maxSplits);
    const shortest = [...segs].sort((a, b) => a.hours - b.hours)[0];
    if (extras > 0 && shortest) {
      const label = shortest.wrapsMidnight
        ? `${minuteLabel(shortest.start)}–${minuteLabel(shortest.end)} (overnight)`
        : `${minuteLabel(shortest.start)}–${minuteLabel(shortest.end)}`;
      return `Suggestion: you have ${periodCount} rest periods (max ${maxSplits}). Merge work around the shortest break (${label}, ${fmtHoursShort(shortest.hours)}) so rest stays in at most ${maxSplits} blocks — e.g. paint through that break or move that work next to another watch.`;
    }
    return `Suggestion: keep at most ${maxSplits} rest periods in any 24h window. Avoid short duty call-outs that split overnight rest (e.g. a drill between 20:00 and 08:00).`;
  }
  if (key === 'REST_SPLIT_LONGEST') {
    const need = Math.round((minLongest - longest) * 100) / 100;
    if (need > 0) {
      return `Suggestion: extend the longest continuous rest by at least ${fmtHoursShort(need)} (currently ${fmtHoursShort(longest)}). Protect one uninterrupted block of ≥ ${minLongest}h — usually the night rest — and avoid work that cuts it short.`;
    }
    return `Suggestion: ensure one continuous rest block of at least ${minLongest}h (typically overnight).`;
  }
  if (key === 'REST_GAP') {
    return `Suggestion: do not work more than ${maxGap}h between rest periods. Insert a rest break before the gap exceeds ${maxGap}h, or end the watch earlier.`;
  }
  if (key === 'REST_7D') {
    const need = Math.round((minRest7 - rest7) * 100) / 100;
    if (need > 0) {
      return `Suggestion: add at least ${fmtHoursShort(need)} more rest somewhere in the last 7 days (reduce overtime on lighter days). Daily rest can still look fine while the weekly total falls short.`;
    }
  }
  return undefined;
}

function buildComplianceChecks(
  cell: CrewDayCell,
  codes: string[],
  rules?: Sheet['rules'],
): ComplianceCheck[] {
  const codeSet = new Set(codes);
  const minRest24 = Number(rules?.minRest24h ?? 10);
  const maxWork24 = Number(rules?.maxWork24h ?? 14);
  const minRest7 = Number(rules?.minRest7d ?? 77);
  const maxSplits = Number(rules?.maxRestSplits ?? 2);
  const minLongest = Number(rules?.minLongestRestSegment ?? 6);
  const maxGap = Number(rules?.maxGapBetweenRest ?? 14);

  const rest24 = Number(cell.restHours);
  const work24 = Number(cell.workHours);
  const rest7 = Number(cell.restHours7d);
  const segs = stcwRestPeriodsFromIntervals(cell.intervals ?? []);
  const periodCount = segs.length || 1;
  const longest = segs.reduce((m, s) => Math.max(m, s.hours), 0);
  const periodLines = segs.length
    ? segs
        .map((s) =>
          s.wrapsMidnight
            ? `• ${minuteLabel(s.start)}–${minuteLabel(s.end)} (${s.hours}h, overnight)`
            : `• ${minuteLabel(s.start)}–${minuteLabel(s.end)} (${s.hours}h)`,
        )
        .join('\n')
    : '• Full day rest (no work painted)';

  const hasSplitCode = codeSet.has('REST_SPLIT');
  let splitOk = periodCount <= maxSplits;
  let longestOk = segs.length === 0 || longest + 1e-9 >= minLongest;
  if (hasSplitCode && splitOk && longestOk) {
    splitOk = false;
  } else if (hasSplitCode) {
    if (periodCount > maxSplits) splitOk = false;
    if (segs.length > 0 && longest + 1e-9 < minLongest) longestOk = false;
  }

  const rest24Ok = !codeSet.has('REST_24H') && rest24 + 1e-9 >= minRest24;
  const work24Ok = !codeSet.has('WORK_24H') && work24 - 1e-9 <= maxWork24;
  const rest7Ok = !codeSet.has('REST_7D') && rest7 + 1e-9 >= minRest7;
  const gapOk = !codeSet.has('REST_GAP');

  const tipCtx = {
    rest24,
    work24,
    rest7,
    minRest24,
    maxWork24,
    minRest7,
    maxSplits,
    minLongest,
    maxGap,
    segs,
    periodCount,
    longest,
  };

  const mk = (
    key: string,
    title: string,
    ok: boolean,
    detail: string,
  ): ComplianceCheck => ({
    key,
    title,
    ok,
    detail,
    suggestion: ok ? undefined : suggestionForCheck(key, tipCtx),
  });

  return [
    mk(
      'REST_24H',
      'Daily rest (10h rule)',
      rest24Ok,
      rest24Ok
        ? `Rest in 24h is ${fmtHoursShort(rest24)} (minimum ${minRest24}h).`
        : violationHint('REST_24H', rules, cell),
    ),
    mk(
      'WORK_24H',
      'Daily work (14h rule)',
      work24Ok,
      work24Ok
        ? `Work in 24h is ${fmtHoursShort(work24)} (maximum ${maxWork24}h).`
        : violationHint('WORK_24H', rules, cell),
    ),
    mk(
      'REST_SPLIT_COUNT',
      'Rest splitting (2-block rule)',
      splitOk,
      splitOk
        ? `Rest is in ${periodCount} period${periodCount === 1 ? '' : 's'} (maximum ${maxSplits}).\n${periodLines}`
        : `${violationHint('REST_SPLIT', rules, cell)}\n${periodLines}`,
    ),
    mk(
      'REST_SPLIT_LONGEST',
      'Main sleep block (6h rule)',
      longestOk,
      longestOk
        ? segs.length === 0
          ? `Full-day rest exceeds the ${minLongest}h continuous minimum.`
          : `Longest continuous rest is ${fmtHoursShort(longest)} (requires ≥ ${minLongest}h).`
        : violationHint('REST_SPLIT', rules, cell),
    ),
    mk(
      'REST_GAP',
      'Interval between rest (14h rule)',
      gapOk,
      gapOk
        ? `Gaps between rest periods are within the ${maxGap}h maximum.`
        : violationHint('REST_GAP', rules, cell),
    ),
    mk(
      'REST_7D',
      'Weekly rest (77h rule)',
      rest7Ok,
      rest7Ok
        ? `Rest over 7 days is ${fmtHoursShort(rest7)} (minimum ${minRest7}h).`
        : violationHint('REST_7D', rules, cell),
    ),
  ];
}

function ComplianceCheckRow({ check }: { check: ComplianceCheck }) {
  return (
    <div
      className={cn(
        'rounded-xl border px-3 py-2.5',
        check.ok
          ? 'border-emerald-500/25 bg-emerald-500/[0.07]'
          : 'border-rose-500/25 bg-rose-500/[0.07]',
      )}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={cn(
            'mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
            check.ok
              ? 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300'
              : 'bg-rose-500/20 text-rose-700 dark:text-rose-300',
          )}
          aria-hidden
        >
          {check.ok ? '✓' : '✕'}
        </span>
        <div className="min-w-0 space-y-1">
          <p
            className={cn(
              'text-sm font-semibold leading-snug',
              check.ok
                ? 'text-emerald-950 dark:text-emerald-100'
                : 'text-rose-950 dark:text-rose-100',
            )}
          >
            {check.title}
          </p>
          <p
            className={cn(
              'whitespace-pre-line text-[12px] leading-snug',
              check.ok
                ? 'text-emerald-900/80 dark:text-emerald-200/85'
                : 'text-rose-900/80 dark:text-rose-200/85',
            )}
          >
            {check.detail}
          </p>
        </div>
      </div>
    </div>
  );
}

function ComplianceChecksGrid({ checks }: { checks: ComplianceCheck[] }) {
  const passed = checks.filter((c) => c.ok);
  const failed = checks.filter((c) => !c.ok);
  const tips = failed.filter((c) => Boolean(c.suggestion));

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <section className="space-y-3 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
        <header className="flex items-center gap-2 border-b border-emerald-500/15 pb-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500/20 text-xs font-bold text-emerald-700 dark:text-emerald-300">
            ✓
          </span>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">
              Compliant
            </h3>
            <p className="text-[11px] text-emerald-800/70 dark:text-emerald-300/70">
              {passed.length} of {checks.length} checks
            </p>
          </div>
        </header>
        {passed.length ? (
          <div className="space-y-2">{passed.map((c) => <ComplianceCheckRow key={c.key} check={c} />)}</div>
        ) : (
          <p className="px-1 py-6 text-center text-xs text-zinc-500 dark:text-zinc-400">
            No compliant checks
          </p>
        )}
      </section>

      <section className="space-y-3 rounded-2xl border border-rose-500/20 bg-rose-500/[0.04] p-3">
        <header className="flex items-center gap-2 border-b border-rose-500/15 pb-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-rose-500/20 text-xs font-bold text-rose-700 dark:text-rose-300">
            ✕
          </span>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-rose-800 dark:text-rose-300">
              Non-compliant
            </h3>
            <p className="text-[11px] text-rose-800/70 dark:text-rose-300/70">
              {failed.length} of {checks.length} checks
            </p>
          </div>
        </header>
        {failed.length ? (
          <div className="space-y-2">{failed.map((c) => <ComplianceCheckRow key={c.key} check={c} />)}</div>
        ) : (
          <p className="px-1 py-6 text-center text-xs text-zinc-500 dark:text-zinc-400">
            No non-compliant checks
          </p>
        )}
      </section>

      <section className="space-y-3 rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] p-3">
        <header className="flex items-center gap-2 border-b border-amber-500/20 pb-2">
          <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-500/20 text-xs font-bold text-amber-800 dark:text-amber-200">
            !
          </span>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
              Suggestions
            </h3>
            <p className="text-[11px] text-amber-900/70 dark:text-amber-200/70">
              Rule-based fixes · no AI
            </p>
          </div>
        </header>
        {tips.length ? (
          <ol className="space-y-2.5">
            {tips.map((c, idx) => (
              <li
                key={c.key}
                className="rounded-xl border border-amber-500/20 bg-white/60 px-3 py-2.5 dark:bg-zinc-950/40"
              >
                <div className="mb-1 flex items-baseline gap-2">
                  <span className="text-[10px] font-bold tabular-nums text-amber-700/80 dark:text-amber-300/80">
                    {idx + 1}.
                  </span>
                  <p className="text-[12px] font-semibold text-amber-950 dark:text-amber-100">
                    {c.title}
                  </p>
                </div>
                <p className="text-[12px] leading-snug text-amber-950/85 dark:text-amber-100/85">
                  {c.suggestion}
                </p>
              </li>
            ))}
          </ol>
        ) : (
          <p className="px-1 py-6 text-center text-xs text-zinc-500 dark:text-zinc-400">
            No suggestions — day meets all checks
          </p>
        )}
      </section>
    </div>
  );
}

function DayIssuesBadge({
  codes,
  rules,
  cell,
}: {
  codes: string[];
  rules?: Sheet['rules'];
  cell?: CrewDayCell | null;
}) {
  const [open, setOpen] = useState(false);
  const compliant = !codes.length;
  const checks = cell ? buildComplianceChecks(cell, codes, rules) : [];

  return (
    <>
      {compliant ? (
        <Hint side="right" content="Compliant — click for checklist" triggerClassName="cursor-pointer">
          <button
            type="button"
            className="mt-0.5 inline-flex h-4 cursor-pointer items-center rounded px-1 text-[9px] font-semibold text-emerald-700/80 hover:bg-emerald-500/10 dark:text-emerald-400/80"
            aria-label="Open compliance checklist"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          >
            OK
          </button>
        </Hint>
      ) : (
        <Hint
          side="right"
          maxWidth={360}
          label="Compliance issue"
          triggerClassName="cursor-pointer"
          content={
            <div className="space-y-1.5 text-left">
              {codes.map((c) => (
                <p key={c} className="whitespace-pre-line text-[11px] leading-snug">
                  <span className="font-semibold">{VIOLATION_META[c]?.short ?? c}: </span>
                  {violationHint(c, rules, cell)}
                </p>
              ))}
            </div>
          }
        >
          <button
            type="button"
            className="mt-0.5 inline-flex max-w-full cursor-pointer items-center gap-0.5 rounded text-left hover:bg-rose-500/10"
            aria-label="Open compliance checklist"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(true);
            }}
          >
            <span className="inline-flex h-4 shrink-0 items-center rounded bg-rose-500/15 px-1 text-[9px] font-bold text-rose-700 dark:text-rose-400">
              !
            </span>
            <span className="truncate text-[9px] font-medium leading-tight text-rose-700 dark:text-rose-400">
              {codes.map((c) => VIOLATION_META[c]?.short ?? c).join(' · ')}
            </span>
          </button>
        </Hint>
      )}
      <Modal isOpen={open} onClose={() => setOpen(false)} size="3xl">
        <ModalHeader>
          <ModalTitle>
            {compliant ? 'Why this day is compliant' : 'Why this day is non-compliant'}
          </ModalTitle>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            title="Close"
            aria-label="Close"
            onClick={() => setOpen(false)}
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </ModalHeader>
        <ModalContent className="space-y-4">
          <ComplianceChecksGrid checks={checks} />
        </ModalContent>
      </Modal>
    </>
  );
}

function NotesCell({
  cell,
  readOnly,
  onSaved,
}: {
  cell: CrewDayCell;
  readOnly: boolean;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(cell.notes ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(cell.notes ?? '');
  }, [cell.crewDayId, cell.notes]);

  const save = async () => {
    const next = value.trim();
    const prev = (cell.notes ?? '').trim();
    if (next === prev || readOnly) return;
    setSaving(true);
    try {
      await updateWrhCrewDay(cell.wrhDayId, cell.crewDayId, { notes: next || null });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="text"
      value={value}
      disabled={readOnly || saving}
      maxLength={500}
      placeholder={readOnly ? '' : 'Add note…'}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => void save()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      className={cn(
        'h-7 w-full rounded-md bg-transparent px-1.5 text-[11px] outline-none transition-colors',
        'placeholder:text-zinc-400 dark:placeholder:text-zinc-500',
        'hover:bg-black/[0.03] focus:bg-white focus:ring-1 focus:ring-sky-500/35 dark:hover:bg-white/[0.04] dark:focus:bg-zinc-900',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
      title={readOnly ? 'Day is closed' : 'Notes'}
    />
  );
}

const MonthSheetPage: React.FC<{ lockedVesselId?: number }> = ({ lockedVesselId }) => {
  const { colors } = useTheme();
  const confirm = useConfirm();
  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>('/crew/lookups', fetcher, {
    revalidateOnFocus: false,
  });
  const [vesselId, setVesselId] = useState(lockedVesselId ? String(lockedVesselId) : '');
  const initialPeriod = nowParts();
  const [year, setYear] = useState(initialPeriod.year);
  const [month, setMonth] = useState(initialPeriod.month);
  const [selectedCrewId, setSelectedCrewId] = useState<number | null>(null);
  const [selections, setSelections] = useState<CellSelection[]>([]);
  const [selectionAnchor, setSelectionAnchor] = useState<{ date: string; slot: number } | null>(null);
  const [applying, setApplying] = useState(false);
  const [pinMetrics, setPinMetrics] = useState(true);
  const [applied, setApplied] = useState<{ vesselId: string; month: string } | null>(
    lockedVesselId
      ? { vesselId: String(lockedVesselId), month: toPeriodYm(initialPeriod.year, initialPeriod.month) }
      : null,
  );

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const years: Array<{ value: string; label: string }> = [];
    for (let y = current + 1; y >= current - 10; y -= 1) {
      years.push({ value: String(y), label: String(y) });
    }
    return years;
  }, []);


  const key = applied
    ? `/wrh/sheet?vesselId=${applied.vesselId}&month=${applied.month}`
    : null;
  const { data, mutate, isLoading } = useSWR<Sheet>(key, fetcher, { revalidateOnFocus: false });

  const [ensureState, setEnsureState] = useState<{
    active: boolean;
    total: number;
    done: number;
    currentDate: string | null;
    error: string | null;
  }>({ active: false, total: 0, done: 0, currentDate: null, error: null });
  const ensureRunKeyRef = useRef<string | null>(null);
  const ensureAbortRef = useRef(0);

  const [fillState, setFillState] = useState<{
    active: boolean;
    mode: 'empty' | 'arrangements' | null;
    total: number;
    done: number;
    currentDate: string | null;
    error: string | null;
    affectedTotal: number;
  }>({
    active: false,
    mode: null,
    total: 0,
    done: 0,
    currentDate: null,
    error: null,
    affectedTotal: 0,
  });
  const fillAbortRef = useRef(0);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!applied || !data || isLoading) return;
    const runKey = `${applied.vesselId}:${applied.month}`;
    if (ensureRunKeyRef.current === runKey) return;

    const missing = data.dates.filter(
      (date) => !data.crew.some((c) => Boolean(c.days[date])),
    );
    if (missing.length === 0) {
      ensureRunKeyRef.current = runKey;
      return;
    }

    const vesselIdNum = Number(applied.vesselId);
    if (!Number.isFinite(vesselIdNum) || vesselIdNum <= 0) return;

    ensureRunKeyRef.current = runKey;
    const runId = ++ensureAbortRef.current;
    setEnsureState({
      active: true,
      total: missing.length,
      done: 0,
      currentDate: missing[0] ?? null,
      error: null,
    });

    void (async () => {
      try {
        for (let i = 0; i < missing.length; i += 1) {
          if (ensureAbortRef.current !== runId) return;
          const workDate = missing[i];
          setEnsureState((prev) => ({
            ...prev,
            currentDate: workDate,
            done: i,
          }));
          await ensureWrhSheetDay({ vesselId: vesselIdNum, workDate });
          setEnsureState((prev) => ({
            ...prev,
            done: i + 1,
          }));
        }
        if (ensureAbortRef.current !== runId) return;
        await mutate();
        setEnsureState({
          active: false,
          total: 0,
          done: 0,
          currentDate: null,
          error: null,
        });
      } catch (err) {
        if (ensureAbortRef.current !== runId) return;
        ensureRunKeyRef.current = null;
        const msg =
          (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
            ?.message;
        setEnsureState((prev) => ({
          ...prev,
          active: true,
          error: Array.isArray(msg)
            ? msg.join(' ')
            : typeof msg === 'string'
              ? msg
              : 'Could not prepare WRH days for this month.',
        }));
      }
    })();
  }, [applied, data, isLoading, mutate]);

  useEffect(() => {
    if (!optionsOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (optionsRef.current?.contains(t)) return;
      setOptionsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [optionsOpen]);

  const runMonthFill = async (mode: 'empty' | 'arrangements') => {
    if (!data?.dates.length || !applied) return;
    setOptionsOpen(false);

    const ok = await confirm(
      mode === 'empty'
        ? {
            title: 'Clear work periods',
            message:
              'Clear all work periods for this month? Every crew day will become empty (all rest). This cannot be undone from here.',
            confirmText: 'Empty',
            cancelText: 'Cancel',
            confirmVariant: 'danger',
          }
        : {
            title: 'Apply working arrangements',
            message:
              'Apply working arrangements to empty days in this month? Crew days that already have work periods will be left unchanged.',
            confirmText: 'Apply',
            cancelText: 'Cancel',
            confirmVariant: 'default',
          },
    );
    if (!ok) return;

    const vesselIdNum = Number(applied.vesselId);
    if (!Number.isFinite(vesselIdNum) || vesselIdNum <= 0) return;

    const dates = [...data.dates];
    const runId = ++fillAbortRef.current;
    setFillState({
      active: true,
      mode,
      total: dates.length,
      done: 0,
      currentDate: dates[0] ?? null,
      error: null,
      affectedTotal: 0,
    });

    void (async () => {
      let affectedTotal = 0;
      try {
        for (let i = 0; i < dates.length; i += 1) {
          if (fillAbortRef.current !== runId) return;
          const workDate = dates[i];
          setFillState((prev) => ({
            ...prev,
            currentDate: workDate,
            done: i,
          }));
          if (mode === 'empty') {
            const res = await clearWrhSheetDayIntervals({ vesselId: vesselIdNum, workDate });
            affectedTotal += Number(res.clearedCount || 0);
          } else {
            const res = await seedWrhSheetDayArrangements({ vesselId: vesselIdNum, workDate });
            affectedTotal += Number(res.seededCount || 0);
          }
          setFillState((prev) => ({
            ...prev,
            done: i + 1,
            affectedTotal,
          }));
        }
        if (fillAbortRef.current !== runId) return;
        await mutate();
        setFillState({
          active: false,
          mode: null,
          total: 0,
          done: 0,
          currentDate: null,
          error: null,
          affectedTotal: 0,
        });
      } catch (err) {
        if (fillAbortRef.current !== runId) return;
        const msg =
          (err as { response?: { data?: { message?: string | string[] } } })?.response?.data
            ?.message;
        setFillState((prev) => ({
          ...prev,
          active: true,
          error: Array.isArray(msg)
            ? msg.join(' ')
            : typeof msg === 'string'
              ? msg
              : mode === 'empty'
                ? 'Could not clear work periods for this month.'
                : 'Could not apply working arrangements for this month.',
          affectedTotal,
        }));
      }
    })();
  };

  const vesselOptions = useMemo(
    () => (lookups?.vessels ?? []).map((v) => ({ value: String(v.id), label: v.vesselName })),
    [lookups?.vessels],
  );

  const selectedCrew = useMemo(() => {
    if (!data?.crew?.length) return null;
    return data.crew.find((c) => c.crewId === selectedCrewId) ?? data.crew[0];
  }, [data, selectedCrewId]);

  const overlayByDate = useMemo(() => {
    const m = new Map<string, Overlay>();
    for (const o of data?.operationOverlays ?? []) m.set(o.workDate, o);
    return m;
  }, [data]);

  const ncDays = useMemo(() => {
    if (!selectedCrew) return 0;
    return Object.values(selectedCrew.days).filter((d) => d && !d.compliant).length;
  }, [selectedCrew]);

  const monthTotals = useMemo(() => {
    if (!selectedCrew || !data?.dates?.length) {
      return { rest: 0, work: 0, ot: 0, dayCount: 0 };
    }
    let work = 0;
    let ot = 0;
    let dayCount = 0;
    for (const date of data.dates) {
      const cell = selectedCrew.days[date];
      if (!cell) continue;
      dayCount += 1;
      work += calendarDayWorkHours(cell.intervals);
      ot += Number(cell.overtimeHours) || 0;
    }
    const rest = dayCount * 24 - work;
    return {
      rest: roundTotal(rest),
      work: roundTotal(work),
      ot: roundTotal(ot),
      dayCount,
    };
  }, [selectedCrew, data]);

  const selectionsByDate = useMemo(() => {
    const m = new Map<string, TimeSegment[]>();
    for (const s of selections) {
      const list = m.get(s.date) ?? [];
      list.push({ startMinute: s.startMinute, endMinute: s.endMinute });
      m.set(s.date, list);
    }
    return m;
  }, [selections]);

  const selectedSlotCount = useMemo(() => {
    return selections.reduce((n, s) => n + Math.max(0, (s.endMinute - s.startMinute) / 30), 0);
  }, [selections]);

  const applyToSelections = async (optionId: string | null) => {
    if (!selectedCrew || selections.length === 0) return;
    setApplying(true);
    try {
      const byDate = new Map<string, CellSelection[]>();
      for (const s of selections) {
        const list = byDate.get(s.date) ?? [];
        list.push(s);
        byDate.set(s.date, list);
      }

      for (const entry of Array.from(byDate.entries())) {
        const [date, ranges] = entry;
        const cell = selectedCrew.days[date];
        if (!cell || cell.status === 'CLOSED') continue;

        let workSegs: TimeSegment[] = cell.intervals.map((iv) => ({
          startMinute: iv.startMinute,
          endMinute: iv.endMinute > 24 * 60 ? 24 * 60 : iv.endMinute,
        }));

        for (const range of ranges) {
          if (optionId === 'W' || optionId === 'D') {
            workSegs = paintSegment(workSegs, range.startMinute, range.endMinute, {
              startMinute: range.startMinute,
              endMinute: range.endMinute,
            });
          } else {
            workSegs = paintSegment(workSegs, range.startMinute, range.endMinute, null);
          }
        }

        const intervals = workSegs.map((s) => ({
          startTime: formatHm(s.startMinute),
          endTime: formatHm(s.endMinute >= 24 * 60 ? 0 : s.endMinute),
          isException: optionId === 'D',
        }));

        await updateWrhIntervals(cell.wrhDayId, cell.crewDayId, { intervals });
      }

      setSelections([]);
      setSelectionAnchor(null);
      await mutate();
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-2', colors.background)}>
      {/* Toolbar */}
      <div
        className={cn(
          'flex shrink-0 flex-wrap items-center gap-2 rounded-xl border px-3 py-2',
          colors.border,
          colors.surface,
        )}
      >
        {!lockedVesselId && (
          <div className="w-[11rem]">
            <Select
              value={vesselId}
              onChange={(e) => setVesselId(e.target.value)}
              options={vesselOptions}
              placeholder="Vessel"
              showClearButton={false}
            />
          </div>
        )}
        <div className="w-[6.5rem]">
          <Select
            value={year}
            onChange={(e) => setYear(e.target.value)}
            options={yearOptions}
            showClearButton={false}
          />
        </div>
        <div className="w-[9.5rem]">
          <Select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            options={MONTH_OPTIONS}
            showClearButton={false}
          />
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            const vid = lockedVesselId ? String(lockedVesselId) : vesselId;
            if (!vid) return;
            ensureAbortRef.current += 1;
            ensureRunKeyRef.current = null;
            setEnsureState({
              active: false,
              total: 0,
              done: 0,
              currentDate: null,
              error: null,
            });
            setApplied({ vesselId: vid, month: toPeriodYm(year, month) });
            setSelectedCrewId(null);
            setSelections([]);
            setSelectionAnchor(null);
          }}
        >
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>

        {data && (
          <>
            <div className={cn('mx-1 hidden h-6 w-px sm:block', 'bg-black/10 dark:bg-white/10')} />
            <div className="min-w-0 flex-1">
              {selectedCrew ? (
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className={cn('truncate text-sm font-semibold', colors.text)}>
                    {selectedCrew.crewName}
                  </span>
                  <span className={cn('truncate text-xs', colors.textMuted)}>
                    {selectedCrew.rankName || '—'}
                    {selectedCrew.watchkeeper ? ' · WK' : ''}
                  </span>
                  {ncDays > 0 ? (
                    <span className="text-xs font-medium text-rose-600 dark:text-rose-400">
                      {ncDays} NC
                    </span>
                  ) : (
                    <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      Compliant
                    </span>
                  )}
                </div>
              ) : (
                <span className={cn('text-xs', colors.textMuted)}>Select a crew member</span>
              )}
            </div>
          </>
        )}

        {data && (
          <div className="relative" ref={optionsRef}>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={ensureState.active || fillState.active || !data.crew.length}
              aria-haspopup="menu"
              aria-expanded={optionsOpen}
              onClick={() => setOptionsOpen((v) => !v)}
            >
              <CogIcon className="h-4 w-4" />
              Options
              <ChevronDownIcon
                className={cn('h-3.5 w-3.5 transition-transform', optionsOpen && 'rotate-180')}
              />
            </Button>
            {optionsOpen ? (
              <div
                role="menu"
                className={cn(
                  'absolute right-0 z-30 mt-1 min-w-[13rem] overflow-hidden rounded-md border py-1 shadow-lg',
                  colors.surface,
                  colors.border,
                )}
              >
                <button
                  type="button"
                  role="menuitem"
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/10',
                    colors.text,
                  )}
                  onClick={() => void runMonthFill('empty')}
                >
                  <TrashIcon className="h-4 w-4 shrink-0 opacity-70" />
                  Empty
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/10',
                    colors.text,
                  )}
                  onClick={() => void runMonthFill('arrangements')}
                >
                  <TableIcon className="h-4 w-4 shrink-0 opacity-70" />
                  Working Arrangements
                </button>
                <div className={cn('my-1 border-t', colors.border)} />
                <Link
                  role="menuitem"
                  to={`/wrh/sheet/rest-record?vesselId=${encodeURIComponent(applied!.vesselId)}&month=${encodeURIComponent(applied!.month)}`}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/10',
                    colors.text,
                  )}
                  onClick={() => setOptionsOpen(false)}
                >
                  <DocumentTextIcon className="h-4 w-4 shrink-0 opacity-70" />
                  Rest Record
                </Link>
                {selectedCrew ? (
                  <Link
                    role="menuitem"
                    to={`/wrh/sheet/rest-record?vesselId=${encodeURIComponent(applied!.vesselId)}&month=${encodeURIComponent(applied!.month)}&crewId=${selectedCrew.crewId}`}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-black/5 dark:hover:bg-white/10',
                      colors.text,
                    )}
                    onClick={() => setOptionsOpen(false)}
                  >
                    <UserIcon className="h-4 w-4 shrink-0 opacity-70" />
                    This Seafarer
                  </Link>
                ) : (
                  <span
                    role="menuitem"
                    aria-disabled="true"
                    className={cn(
                      'flex w-full cursor-not-allowed items-center gap-2 px-3 py-2 text-left text-sm opacity-50',
                      colors.textMuted,
                    )}
                    title="Select a crew member on the sheet first"
                  >
                    <UserIcon className="h-4 w-4 shrink-0" />
                    This Seafarer
                  </span>
                )}
              </div>
            ) : null}
          </div>
        )}

        {data && (
          <Button
            type="button"
            size="icon"
            variant={pinMetrics ? 'secondary' : 'ghost'}
            className="h-8 w-8 shrink-0"
            title={
              pinMetrics
                ? 'Unpin metrics columns so they scroll with the sheet'
                : 'Pin metrics columns to the right'
            }
            aria-label={pinMetrics ? 'Unpin metrics' : 'Pin metrics'}
            aria-pressed={pinMetrics}
            onClick={() => setPinMetrics((v) => !v)}
          >
            {pinMetrics ? <PinIcon className="h-4 w-4" /> : <PinOffIcon className="h-4 w-4" />}
          </Button>
        )}
      </div>

      <Modal
        isOpen={fillState.active}
        onClose={() => undefined}
        closeOnEscape={false}
        size="sm"
        zIndex={120}
      >
        <ModalHeader>
          <ModalTitle>
            {fillState.error
              ? fillState.mode === 'empty'
                ? 'Could not clear work'
                : 'Could not apply arrangements'
              : fillState.mode === 'empty'
                ? 'Clearing work periods'
                : 'Applying working arrangements'}
          </ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <p className={cn('text-sm leading-relaxed', colors.textSecondary)}>
              {fillState.error
                ? fillState.error
                : fillState.mode === 'empty'
                  ? 'Removing work periods for each day so the month is empty (all rest).'
                  : 'Filling empty work periods from crew working arrangements (watchkeeper / non-watchkeeper × at sea / in port). Existing painted work is left unchanged.'}
            </p>
            <Progress
              value={fillState.done}
              max={Math.max(fillState.total, 1)}
              size="lg"
              showLabel
              label={
                fillState.error
                  ? `Stopped at ${fillState.done} of ${fillState.total}`
                  : fillState.currentDate
                    ? `${formatDate(fillState.currentDate)} · ${fillState.done} of ${fillState.total}`
                    : `${fillState.done} of ${fillState.total}`
              }
            />
            {!fillState.error ? (
              <p className={cn('text-xs', colors.textMuted)}>
                {fillState.mode === 'empty' ? 'Cleared' : 'Seeded'} {fillState.affectedTotal}{' '}
                {fillState.mode === 'empty' ? 'interval' : 'crew-day'}
                {fillState.affectedTotal === 1 ? '' : 's'} so far. Please keep this window open.
              </p>
            ) : null}
          </div>
        </ModalContent>
        {fillState.error ? (
          <div className={cn('flex shrink-0 items-center justify-end gap-3 border-t p-6', colors.border)}>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                fillAbortRef.current += 1;
                setFillState({
                  active: false,
                  mode: null,
                  total: 0,
                  done: 0,
                  currentDate: null,
                  error: null,
                  affectedTotal: 0,
                });
              }}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => {
                fillAbortRef.current += 1;
                setFillState({
                  active: false,
                  mode: null,
                  total: 0,
                  done: 0,
                  currentDate: null,
                  error: null,
                  affectedTotal: 0,
                });
                void mutate();
              }}
            >
              Reload sheet
            </Button>
          </div>
        ) : null}
      </Modal>

      {isLoading && !ensureState.active && !fillState.active && (
        <p className={cn('shrink-0 text-sm', colors.textMuted)}>Loading sheet…</p>
      )}

      <Modal
        isOpen={ensureState.active}
        onClose={() => undefined}
        closeOnEscape={false}
        size="sm"
        zIndex={120}
      >
        <ModalHeader>
          <ModalTitle>
            {ensureState.error ? 'Could not prepare sheet' : 'Preparing WRH month sheet'}
          </ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <p className={cn('text-sm leading-relaxed', colors.textSecondary)}>
              {ensureState.error
                ? ensureState.error
                : 'No day records found for this vessel and month. Creating open days and crew rows (work stays empty until you apply arrangements or paint)…'}
            </p>
            <Progress
              value={ensureState.done}
              max={Math.max(ensureState.total, 1)}
              size="lg"
              showLabel
              label={
                ensureState.error
                  ? `Stopped at ${ensureState.done} of ${ensureState.total}`
                  : ensureState.currentDate
                    ? `${formatDate(ensureState.currentDate)} · ${ensureState.done} of ${ensureState.total}`
                    : `${ensureState.done} of ${ensureState.total}`
              }
            />
            {!ensureState.error ? (
              <p className={cn('text-xs', colors.textMuted)}>
                This can take a moment for a full month. Please keep this window open.
              </p>
            ) : null}
          </div>
        </ModalContent>
        {ensureState.error ? (
          <div className={cn('flex shrink-0 items-center justify-end gap-3 border-t p-6', colors.border)}>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                ensureAbortRef.current += 1;
                ensureRunKeyRef.current = null;
                setEnsureState({
                  active: false,
                  total: 0,
                  done: 0,
                  currentDate: null,
                  error: null,
                });
              }}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => {
                ensureRunKeyRef.current = null;
                setEnsureState({
                  active: false,
                  total: 0,
                  done: 0,
                  currentDate: null,
                  error: null,
                });
                void mutate();
              }}
            >
              Retry
            </Button>
          </div>
        ) : null}
      </Modal>

      {!applied && !isLoading && (
        <div
          className={cn(
            'flex flex-1 items-center justify-center rounded-xl border border-dashed text-sm',
            colors.border,
            colors.textMuted,
          )}
        >
          Choose vessel and month, then Search.
        </div>
      )}

      {data && !ensureState.active && !fillState.active && (
        <div className="flex min-h-0 flex-1 gap-2">
          {/* Crew rail */}
          <aside
            className={cn(
              'flex w-44 shrink-0 flex-col overflow-hidden rounded-xl border sm:w-52',
              colors.border,
              colors.surface,
            )}
          >
            <div
              className={cn(
                'flex shrink-0 items-center justify-between border-b px-2.5 py-1.5',
                colors.border,
              )}
            >
              <span className={cn('text-[10px] font-semibold uppercase tracking-wide', colors.textSecondary)}>
                Crew
              </span>
              <span className={cn('text-[10px] tabular-nums', colors.textMuted)}>{data.crew.length}</span>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-1">
              {data.crew.map((c) => {
                const hasNc = Object.values(c.days).some((d) => d && !d.compliant);
                const active = selectedCrew?.crewId === c.crewId;
                return (
                  <button
                    key={c.crewId}
                    type="button"
                    className={cn(
                      'mb-0.5 flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors',
                      active ? 'bg-sky-500/15 ring-1 ring-sky-500/30' : 'hover:bg-black/[0.04] dark:hover:bg-white/[0.04]',
                      colors.text,
                    )}
                    onClick={() => {
                      setSelectedCrewId(c.crewId);
                      setSelections([]);
                      setSelectionAnchor(null);
                    }}
                  >
                    <span
                      className={cn(
                        'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
                        hasNc ? 'bg-rose-500' : 'bg-emerald-500',
                      )}
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium leading-tight">{c.crewName}</span>
                      <span className={cn('block truncate text-[10px]', colors.textMuted)}>
                        {c.rankName || '—'}
                        {c.watchkeeper ? ' · WK' : ''}
                      </span>
                    </span>
                  </button>
                );
              })}
              {data.crew.length === 0 && (
                <p className={cn('px-2 py-4 text-xs', colors.textMuted)}>No crew in this month.</p>
              )}
            </div>
            <div
              className={cn(
                'flex shrink-0 gap-2.5 border-t px-2.5 py-1.5 text-[10px]',
                colors.border,
                colors.textMuted,
              )}
            >
              <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-600" /> W
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-zinc-300 dark:bg-zinc-600" /> R
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Exc
              </span>
            </div>
          </aside>

          {/* Month grid */}
          <div
            className={cn(
              'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border',
              colors.border,
              colors.surface,
            )}
          >
            {selectedCrew ? (
              <div className="min-h-0 flex-1 overflow-auto">
                <div
                  className={cn(
                    'sticky top-0 z-10 flex min-w-max items-stretch border-b bg-white/95 backdrop-blur-sm dark:bg-zinc-950/95',
                    colors.border,
                  )}
                >
                  <div
                    className={cn(
                      'sticky left-0 z-20 flex shrink-0 items-end px-2 pb-1.5 pt-2 text-[10px] font-semibold uppercase tracking-wide',
                      colors.textSecondary,
                      'bg-white/95 dark:bg-zinc-950/95',
                    )}
                    style={{ width: DATE_COL }}
                  >
                    Day
                  </div>
                  <div className="min-w-0 flex-1 px-1 pb-1 pt-1.5" style={{ minWidth: HOURS_W }}>
                    <TimelineHourHeader slotMinutes={30} fillWidth cellMinWidth={SLOT_W} />
                  </div>
                  <div
                    className={cn(
                      'flex shrink-0 self-stretch border-l border-black/[0.08] bg-zinc-100/95 dark:border-white/[0.1] dark:bg-zinc-900/95',
                      pinMetrics && 'sticky right-0 z-20',
                    )}
                    style={{ width: COMPLY_COLS_W }}
                  >
                    {COMPLY_HEADERS.map((h) => (
                      <div
                        key={h.key}
                        className={cn(
                          'flex flex-col items-center justify-center border-l border-black/[0.06] px-0.5 py-1 text-center dark:border-white/[0.08]',
                          h.key === 'notes' && 'items-start px-2',
                        )}
                        style={{ width: COL[h.key] }}
                        title={h.title}
                      >
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-600 dark:text-zinc-300">
                          {h.line1}
                        </span>
                        {h.line2 ? (
                          <span className="text-[9px] font-medium text-zinc-400 dark:text-zinc-500">
                            {h.line2}
                          </span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                {data.dates.map((date, idx) => {
                  const cell = selectedCrew.days[date];
                  const overlay = overlayByDate.get(date);
                  const workSegs: TimeSegment[] = (cell?.intervals ?? []).map((iv) => ({
                    startMinute: iv.startMinute,
                    endMinute: iv.endMinute > 24 * 60 ? 24 * 60 : iv.endMinute,
                  }));
                  const isWeekend = [0, 6].includes(new Date(`${date}T12:00:00Z`).getUTCDay());
                  const codes = cell?.violationCodes ?? [];
                  const hasIssues = Boolean(cell && !cell.compliant && codes.length > 0);
                  const codeSet = new Set(codes);
                  return (
                    <div
                      key={date}
                      className={cn(
                        'flex min-w-max items-stretch border-b border-black/[0.04] dark:border-white/[0.04]',
                        isWeekend && 'bg-amber-500/[0.04]',
                        idx % 2 === 1 && !isWeekend && 'bg-black/[0.015] dark:bg-white/[0.02]',
                        hasIssues && 'bg-rose-500/[0.04]',
                      )}
                    >
                      <div
                        className={cn(
                          'sticky left-0 z-[5] flex shrink-0 flex-col justify-center border-l-2 px-2 py-0.5',
                          hasIssues
                            ? 'border-l-rose-500 bg-rose-50 dark:bg-rose-950/50'
                            : isWeekend
                              ? 'border-l-transparent bg-amber-50 dark:bg-amber-950/40'
                              : cn('border-l-transparent', colors.surface),
                        )}
                        style={{ width: DATE_COL }}
                      >
                        <div className="flex items-baseline gap-1">
                          <div className={cn('text-sm font-semibold tabular-nums leading-none', colors.text)}>
                            {date.slice(8)}
                          </div>
                          <div className={cn('text-[10px] leading-none', colors.textMuted)}>
                            {weekdayLabel(date)}
                          </div>
                        </div>
                        {cell ? (
                          <DayIssuesBadge codes={codes} rules={data.rules} cell={cell} />
                        ) : (
                          <span className={cn('mt-0.5 text-[9px]', colors.textMuted)}>—</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1 px-1 py-0.5" style={{ minWidth: HOURS_W }}>
                        <TimelinePaintGrid
                          slotMinutes={30}
                          fillWidth
                          cellMinWidth={SLOT_W}
                          cellHeight={CELL_H}
                          segments={workSegs}
                          slotBackgrounds={buildSlotBackgrounds(overlay)}
                          selectedRanges={selectionsByDate.get(date) ?? []}
                          selectionAnchorSlot={
                            selectionAnchor?.date === date ? selectionAnchor.slot : null
                          }
                          fillLabel="W"
                          readOnly={!cell || cell.status === 'CLOSED'}
                          onRangeCommit={(range, meta) => {
                            if (!cell) return;
                            const startSlot = Math.floor(range.startMinute / 30);
                            const endSlot = Math.max(0, Math.floor((range.endMinute - 1) / 30));
                            setSelections((prev) =>
                              mergeSelections(
                                prev,
                                {
                                  date,
                                  startMinute: range.startMinute,
                                  endMinute: range.endMinute,
                                },
                                meta.additive,
                              ),
                            );
                            if (!meta.extend) {
                              setSelectionAnchor({ date, slot: startSlot });
                            } else if (!selectionAnchor || selectionAnchor.date !== date) {
                              setSelectionAnchor({ date, slot: startSlot });
                            }
                            void endSlot;
                          }}
                        />
                      </div>
                      <div
                        className={cn(
                          'flex shrink-0 items-stretch self-stretch border-l border-black/[0.08] bg-zinc-50/90 dark:border-white/[0.1] dark:bg-zinc-900/90',
                          pinMetrics && 'sticky right-0 z-[5]',
                        )}
                        style={{ width: COMPLY_COLS_W }}
                      >
                        {cell ? (
                          <>
                            <MetricCell
                              width={COL.rest24}
                              title={
                                codeSet.has('REST_24H')
                                  ? violationHint('REST_24H', data.rules, cell)
                                  : 'Hours of rest in 24 hour period'
                              }
                              tone={codeSet.has('REST_24H') ? 'bad' : 'default'}
                            >
                              {fmtHours(cell.restHours)}
                            </MetricCell>
                            <MetricCell
                              width={COL.work24}
                              title={
                                codeSet.has('WORK_24H')
                                  ? violationHint('WORK_24H', data.rules, cell)
                                  : 'Hours of work in 24 hour period'
                              }
                              tone={codeSet.has('WORK_24H') ? 'bad' : 'default'}
                            >
                              {fmtHours(cell.workHours)}
                            </MetricCell>
                            <MetricCell
                              width={COL.restOk}
                              title={
                                codeSet.has('REST_24H')
                                  ? violationHint('REST_24H', data.rules, cell)
                                  : 'Hours of rest in any 24 hours period (at least 10) Y/N'
                              }
                            >
                              <YnPill
                                ok={Number(cell.restHours) + 1e-9 >= (data.rules?.minRest24h ?? 10)}
                              />
                            </MetricCell>
                            <MetricCell
                              width={COL.rest7d}
                              title={
                                codeSet.has('REST_7D')
                                  ? violationHint('REST_7D', data.rules, cell)
                                  : 'Hours of rest in any 7 day period'
                              }
                              tone={codeSet.has('REST_7D') ? 'bad' : 'default'}
                            >
                              {fmtHours(cell.restHours7d)}
                            </MetricCell>
                            <MetricCell
                              width={COL.ot}
                              title="Overtime — hours above 8h normal working day (all work hours on holidays)"
                              tone={Number(cell.overtimeHours) > 0 ? 'warn' : 'muted'}
                            >
                              {Number(cell.overtimeHours) > 0 ? fmtHours(cell.overtimeHours) : '—'}
                            </MetricCell>
                            <MetricCell
                              width={COL.h}
                              title="Holiday"
                              tone={cell.holidayFlag ? 'default' : 'muted'}
                            >
                              {cell.holidayFlag ? (
                                <span className="inline-flex h-5 w-5 items-center justify-center rounded-md bg-sky-500/15 text-[10px] font-bold text-sky-700 dark:text-sky-400">
                                  H
                                </span>
                              ) : (
                                '·'
                              )}
                            </MetricCell>
                            <div
                              className="flex items-center border-l border-black/[0.06] px-1 dark:border-white/[0.08]"
                              style={{ width: COL.notes }}
                            >
                              <NotesCell
                                cell={cell}
                                readOnly={cell.status === 'CLOSED'}
                                onSaved={() => void mutate()}
                              />
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-1 items-center justify-center text-xs text-zinc-400">
                            —
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div
                  className={cn(
                    'sticky bottom-0 z-10 flex min-w-max items-stretch border-t-2 bg-zinc-100/95 backdrop-blur-sm dark:bg-zinc-900/95',
                    colors.border,
                  )}
                >
                  <div
                    className={cn(
                      'sticky left-0 z-20 flex shrink-0 flex-col justify-center px-2 py-1.5',
                      'bg-zinc-100/95 dark:bg-zinc-900/95',
                    )}
                    style={{ width: DATE_COL }}
                  >
                    <div className={cn('text-xs font-bold uppercase tracking-wide', colors.text)}>
                      Total
                    </div>
                    <div className={cn('text-[9px] tabular-nums', colors.textMuted)}>
                      {monthTotals.dayCount} day{monthTotals.dayCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div
                    className={cn(
                      'flex min-w-0 flex-1 items-center px-2 text-[11px]',
                      colors.textMuted,
                    )}
                    style={{ minWidth: HOURS_W }}
                  >
                    Month sum · calendar rest / work · OT
                  </div>
                  <div
                    className={cn(
                      'flex shrink-0 items-stretch self-stretch border-l border-black/[0.08] bg-zinc-200/90 dark:border-white/[0.1] dark:bg-zinc-800/95',
                      pinMetrics && 'sticky right-0 z-20',
                    )}
                    style={{ width: COMPLY_COLS_W }}
                  >
                    <MetricCell
                      width={COL.rest24}
                      title="Month total rest hours (calendar days)"
                      tone="default"
                    >
                      <span className="font-semibold">{fmtHours(monthTotals.rest)}</span>
                    </MetricCell>
                    <MetricCell
                      width={COL.work24}
                      title="Month total work hours (calendar days)"
                      tone="default"
                    >
                      <span className="font-semibold">{fmtHours(monthTotals.work)}</span>
                    </MetricCell>
                    <MetricCell width={COL.restOk} title="" tone="muted">
                      —
                    </MetricCell>
                    <MetricCell width={COL.rest7d} title="" tone="muted">
                      —
                    </MetricCell>
                    <MetricCell
                      width={COL.ot}
                      title="Month total overtime hours"
                      tone={monthTotals.ot > 0 ? 'warn' : 'muted'}
                    >
                      <span className="font-semibold">
                        {monthTotals.ot > 0 ? fmtHours(monthTotals.ot) : '—'}
                      </span>
                    </MetricCell>
                    <MetricCell width={COL.h} title="" tone="muted">
                      —
                    </MetricCell>
                    <div
                      className="flex items-center border-l border-black/[0.06] px-1 dark:border-white/[0.08]"
                      style={{ width: COL.notes }}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <p className={cn('flex flex-1 items-center justify-center text-sm', colors.textMuted)}>
                Select a crew member to view the month grid.
              </p>
            )}
          </div>
        </div>
      )}

      {selections.length > 0 && (
        <div className="fixed bottom-5 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-2 shadow-2xl dark:border-white/10 dark:bg-zinc-900">
          <span className={cn('px-2 text-xs font-medium tabular-nums', colors.textSecondary)}>
            {selectedSlotCount} cell{selectedSlotCount === 1 ? '' : 's'} · {selections.length} range
            {selections.length === 1 ? '' : 's'}
          </span>
          <Button type="button" size="sm" disabled={applying} onClick={() => void applyToSelections('W')}>
            Work
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={applying}
            onClick={() => void applyToSelections('R')}
          >
            Rest
          </Button>
          <Button
            type="button"
            size="sm"
            variant="warning"
            disabled={applying}
            onClick={() => void applyToSelections('D')}
          >
            Exception
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={applying}
            onClick={() => {
              setSelections([]);
              setSelectionAnchor(null);
            }}
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

export default MonthSheetPage;
